import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDecadeLabel } from "@hitster/shared";
import { useGame } from "../hooks/useGameContext";
import { MysteryTrack } from "../components/MysteryTrack";
import { RevealPanel } from "../components/RevealPanel";
import { GameOverModal } from "../components/GameOverModal";
import { QuitGameModal } from "../components/QuitGameModal";
import { AudioController } from "../components/AudioController";
import { GameTable } from "../components/GameTable";
import { BingoGamePage } from "../components/BingoGamePage";
import { clearPlacementDraft, getPlacementDraft, savePlacementDraft } from "../lib/formDrafts";

export function GamePage() {
  const { game, room, playerId, lastReveal, emitUserAction, leaveLobby, hintDecade, hintsRemaining, audioLoadFailed, audioLoading } = useGame();
  const navigate = useNavigate();
  const [, tick] = useState(0);
  const prevRoundIdRef = useRef<string | null>(null);
  const [dismissedRevealRoundId, setDismissedRevealRoundId] = useState<string | null>(null);
  const [gameOverDismissed, setGameOverDismissed] = useState(false);
  const [quitModalOpen, setQuitModalOpen] = useState(false);

  const roundId = game?.roundId ?? null;
  const roomCode = room?.code ?? "";

  const [selectedIndex, setSelectedIndexState] = useState<number | null>(() => {
    if (!roomCode || !roundId) return null;
    return getPlacementDraft(roomCode, roundId);
  });

  const setSelectedIndex = (index: number | null) => {
    setSelectedIndexState(index);
    if (roomCode && roundId) savePlacementDraft(roomCode, roundId, index);
  };

  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (room && !game) navigate("/lobby", { replace: true });
  }, [room, game, navigate]);

  useEffect(() => {
    if (game?.phase !== "GAME_OVER") setGameOverDismissed(false);
  }, [game?.phase]);

  useEffect(() => {
    if (!roomCode || !roundId) return;
    if (prevRoundIdRef.current && prevRoundIdRef.current !== roundId) {
      clearPlacementDraft(roomCode, prevRoundIdRef.current);
      setSelectedIndexState(null);
    } else {
      setSelectedIndexState(getPlacementDraft(roomCode, roundId));
    }
    prevRoundIdRef.current = roundId;
  }, [roomCode, roundId]);

  useEffect(() => {
    if (!roomCode || !roundId) return;
    if (game?.phase === "REVEAL" || game?.phase === "GAME_OVER") {
      clearPlacementDraft(roomCode, roundId);
      setSelectedIndexState(null);
    }
  }, [game?.phase, roomCode, roundId]);

  if (!game || !room) {
    return <div className="text-center py-20">Chargement de la partie…</div>;
  }

  if ((game.settings.gameKind ?? "timeline") === "bingo") {
    return <BingoGamePage />;
  }

  const me = game.players.find((p) => p.id === playerId);
  const isSpectator = me?.role === "spectator";
  const isHost = playerId === room.hostId;
  const playingCount = game.players.filter((p) => p.role === "player" && !p.hasLeft).length;
  const isSoloGame = playingCount <= 1;
  const isMyTurn = !isSpectator && (me?.isActive ?? false) && !me?.hasWon;
  const canPlace = isMyTurn && (game.phase === "PLACING" || game.phase === "LISTENING");
  const hintsEnabled = !!game.settings.hintsEnabled;
  const maxHints = game.settings.maxHintsPerPlayer ?? 3;
  const hintsUsed = me?.hintsUsed ?? 0;
  const canUseHint =
    isMyTurn &&
    hintsEnabled &&
    hintDecade === null &&
    hintsUsed < maxHints &&
    game.phase !== "REVEAL" &&
    game.phase !== "GAME_OVER";
  const activePlayer = game.players.find((p) => p.isActive);
  const showListening =
    game.listenStarted &&
    game.phase !== "REVEAL" &&
    game.phase !== "GAME_OVER";
  const winTarget = game.settings.winTarget;
  const winner = game.players.find((p) => p.id === game.winnerId);
  const gameContinuesAfterWin =
    game.winnerId !== null &&
    game.phase !== "GAME_OVER" &&
    game.settings.continueAfterWin !== false;
  const revealPlayer = lastReveal
    ? game.players.find((p) => p.id === lastReveal.playerId)
    : undefined;
  const isRevealActivePlayer = lastReveal?.playerId === playerId;
  const showRevealPopup =
    Boolean(lastReveal && dismissedRevealRoundId !== lastReveal.roundId);
  const showGameOverPopup = game.phase === "GAME_OVER" && !gameOverDismissed;

  const handlePlay = () => {
    if (game.roundId) emitUserAction("audio:request-play", { roundId: game.roundId });
  };

  const handleUseHint = () => {
    if (!game.roundId) return;
    emitUserAction("round:use-hint", { roundId: game.roundId });
  };

  const handleConfirm = () => {
    if (selectedIndex === null || !game.roundId) return;
    emitUserAction("round:place-card", { roundId: game.roundId, insertIndex: selectedIndex });
    emitUserAction("round:confirm-placement", { roundId: game.roundId });
  };

  const handleContinue = () => {
    if (!lastReveal || lastReveal.playerId !== playerId) return;
    if (room.code) clearPlacementDraft(room.code, lastReveal.roundId);
    emitUserAction("round:continue", { roundId: lastReveal.roundId });
    setSelectedIndex(null);
    setDismissedRevealRoundId(lastReveal.roundId);
  };

  const handleDismissReveal = () => {
    if (lastReveal) setDismissedRevealRoundId(lastReveal.roundId);
  };

  const handleRestart = () => {
    emitUserAction("game:restart");
  };

  const handleQuit = () => {
    if (isSpectator) {
      leaveLobby();
      return;
    }
    setQuitModalOpen(true);
  };

  const confirmQuit = () => {
    setQuitModalOpen(false);
    leaveLobby();
  };

  const boardPlayers = game.players.filter((p) => p.role === "player");

  return (
    <div className="-mx-4 px-3 sm:px-4 md:-mx-8 md:px-8 w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] space-y-3 sm:space-y-6">
      <header className="cw-panel p-2.5 sm:p-4 space-y-2 sm:space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <div className="min-w-0">
            <p className="text-xs sm:text-sm text-violet-400">
              {game.phase === "GAME_OVER" ? "Partie terminée" : `Manche ${game.roundNumber}`}
            </p>
            <p className="text-sm sm:text-lg truncate">
              {game.phase === "GAME_OVER" ? (
                game.winnerId ? (
                  <>
                    Victoire de{" "}
                    <strong className="text-orange-400">{winner?.nickname ?? "—"}</strong>
                  </>
                ) : (
                  <strong className="text-violet-300">Partie terminée</strong>
                )
              ) : gameContinuesAfterWin ? (
                <>
                  <strong className="text-green-400">{winner?.nickname ?? "—"}</strong> a gagné — la partie continue
                </>
              ) : isSpectator ? (
                <>
                  Spectateur — tour de{" "}
                  <strong className="text-orange-400">
                    {game.players.find((p) => p.isActive)?.nickname ?? "—"}
                  </strong>
                </>
              ) : (
                <>
                  Tour de{" "}
                  <strong className="text-orange-400">
                    {game.players.find((p) => p.isActive)?.nickname ?? "—"}
                  </strong>
                </>
              )}
            </p>
          </div>
          {game.phase !== "GAME_OVER" && (
            <button type="button" className="cw-btn cw-btn-secondary cw-btn-game" onClick={handleQuit}>
              <span className="sm:hidden">Quitter</span>
              <span className="hidden sm:inline">Quitter la partie</span>
            </button>
          )}
        </div>

        {isMyTurn && game.phase !== "GAME_OVER" && game.phase !== "REVEAL" && (
          <div className="flex flex-col items-center gap-1.5 sm:gap-3 w-full pt-1 border-t border-violet-500/20">
            <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-3">
              <AudioController
                canPlay={game.phase === "ROUND_PREPARATION" || game.phase === "LISTENING" || game.phase === "PLACING"}
                canReplay={game.canReplay}
                listenStarted={game.listenStarted}
                audioLoading={audioLoading}
                audioFailed={audioLoadFailed}
                onPlay={handlePlay}
                roundEndsAt={game.roundEndsAt}
                roundDurationSeconds={game.settings.roundDurationSeconds}
              />
              <button
                type="button"
                className="cw-btn cw-btn-primary cw-btn-game disabled:opacity-40"
                disabled={!canPlace || selectedIndex === null}
                onClick={handleConfirm}
              >
                <span className="sm:hidden">Valider</span>
                <span className="hidden sm:inline">Valider mon placement</span>
              </button>
              {hintsEnabled && (
                <button
                  type="button"
                  className="cw-btn cw-btn-secondary cw-btn-game disabled:opacity-40"
                  disabled={!canUseHint}
                  onClick={handleUseHint}
                >
                  Indice
                  {hintsRemaining !== null
                    ? ` (${hintsRemaining})`
                    : ` (${maxHints - hintsUsed})`}
                </button>
              )}
            </div>
            {hintsEnabled && hintDecade !== null && (
              <p className="text-xs sm:text-sm text-orange-300 font-medium">
                Décennie : {formatDecadeLabel(hintDecade)}
              </p>
            )}
          </div>
        )}
      </header>

      {gameContinuesAfterWin && (
        <p className="text-center text-sm text-green-300/90 cw-panel py-2 px-4">
          {winner?.nickname} a atteint l&apos;objectif. Les autres joueurs peuvent continuer à jouer.
        </p>
      )}

      {isSpectator && game.phase !== "GAME_OVER" && game.phase !== "REVEAL" && (
        <p className="text-center text-sm text-violet-300/80 cw-panel py-2 px-4">
          Mode spectateur — vous écoutez les extraits avec les autres joueurs, sans frise ni placement.
        </p>
      )}

      {showListening && (
        <MysteryTrack
          isActivePlayer={isMyTurn}
          activeNickname={activePlayer?.nickname}
        />
      )}

      <GameTable
        players={boardPlayers}
        winTarget={winTarget}
        playerId={playerId}
        canPlace={canPlace}
        selectedIndex={selectedIndex}
        onSelect={setSelectedIndex}
        roundNumber={game.roundNumber}
        mode={game.settings.mode}
        sharedTimeline={game.sharedTimeline}
      />

      {lastReveal && (
        <RevealPanel
          reveal={lastReveal}
          playerNickname={revealPlayer?.nickname ?? "Joueur"}
          open={showRevealPopup}
          canContinue={isRevealActivePlayer}
          continueAfterWin={gameContinuesAfterWin || (game.settings.continueAfterWin !== false && !!lastReveal && lastReveal.newScore >= winTarget)}
          onClose={handleDismissReveal}
          onContinue={handleContinue}
        />
      )}

      <GameOverModal
        open={showGameOverPopup}
        winnerNickname={winner?.nickname ?? "—"}
        abandoned={!game.winnerId}
        onDismiss={() => setGameOverDismissed(true)}
      />

      <QuitGameModal
        open={quitModalOpen}
        solo={isSoloGame}
        onConfirm={confirmQuit}
        onClose={() => setQuitModalOpen(false)}
      />

      {game.phase === "GAME_OVER" && gameOverDismissed && (
        <div className="sticky bottom-4 z-40 cw-panel p-4 flex flex-wrap items-center justify-between gap-4 border border-orange-500/30 shadow-xl">
          <div>
            <p className="font-semibold text-orange-300">Récapitulatif de la partie</p>
            <p className="text-sm text-violet-300/70">
              {isHost
                ? "Relancez une nouvelle partie pour tout le monde."
                : "En attente que l'hôte relance une partie…"}
            </p>
          </div>
          {isHost && (
            <button type="button" className="cw-btn cw-btn-primary" onClick={handleRestart}>
              Nouvelle partie
            </button>
          )}
        </div>
      )}
    </div>
  );
}
